// The Monitoring AUDIT-DERIVED ACTIVITY view (N-FDE-2 AC-3) — the sanitised, hashed-identity kernel Audit
// projection the API already resolved (G-058). Each row shows when, what capability ran, the object touched,
// and the outcome — never a raw secret / token / payload / body / recipient (the projection carries none, so
// there is nothing to leak). The activity view adds nothing and derives nothing (CR-INT-8); it carries NO
// internal/build/third-party identifier (G-197). Tokens only (G-181). An empty view states its emptiness
// honestly (P-SELF) rather than inventing activity.

import { Panel, EmptyState, StatusPill } from '../primitives.tsx';
import { outcomeTone } from './operate-primitives.tsx';
import type { AuditRowView } from '../operate-projections.ts';

export interface MonitoringActivityProps {
  readonly rows: readonly AuditRowView[];
}

/** The audit activity list — an internal-scroll feed of the sanitised, hashed-identity Audit projection. */
export function MonitoringActivity({ rows }: MonitoringActivityProps): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <Panel>
        <EmptyState>No audited activity in this window.</EmptyState>
      </Panel>
    );
  }
  return (
    <Panel className="overflow-hidden">
      <ol
        className="max-h-[32rem] divide-y divide-border overflow-y-auto"
        aria-label="Audit activity feed"
        data-audit-activity
      >
        {rows.map((row) => (
          <li key={row.id} className="flex gap-3 px-5 py-3" data-audit-row={row.id}>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-sm font-medium text-text">{row.capability}</span>
                <span className="ritona-numeric shrink-0 text-xs text-text-subtle">
                  {row.timestamp}
                </span>
              </div>
              <div className="mt-0.5 text-sm text-text-muted">
                {/* The actor is a one-way HASH resolved to a display ref by the API — never a raw identity. */}
                {row.actor}
                {row.objectRef !== null ? ` · ${row.objectRef}` : ''}
              </div>
            </div>
            <span className="shrink-0 self-center">
              <StatusPill tone={outcomeTone(row.outcome)}>{row.outcome}</StatusPill>
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}
